import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@clerk/react'
import {
  useAuthenticatedFetch,
  type Conversation,
  type ConversationTurn,
  type ConversationWithTurns,
  type SendMessageResponse,
} from '../lib/api'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// Pull explanation text out of an in-progress Claude stream so the bubble
// displays only the natural-language part, never the <prompt> XML tags.
function extractStreamingExplanation(text: string): string {
  const open = text.indexOf('<explanation>')
  if (open === -1) return ''
  const after = text.slice(open + '<explanation>'.length)
  const close = after.indexOf('</explanation>')
  return (close === -1 ? after : after.slice(0, close)).trim()
}

interface UseConversationResult {
  // Current open conversation
  conversation: Conversation | null
  turns: ConversationTurn[]
  // Live-streaming assistant text while a send is in flight
  streamingText: string | null
  streamingStage: 'thinking' | 'generating' | null
  // List of recent conversations
  conversations: Conversation[]
  // Flags
  isSending: boolean
  isLoading: boolean
  error: string | null
  // Actions
  startNewConversation: (
    model: string,
    firstMessage?: string,
    imageUrls?: string[],
  ) => Promise<void>
  openConversation: (id: string) => Promise<void>
  resetConversation: () => void
  sendMessage: (message: string, imageUrls?: string[]) => Promise<void>
  clearError: () => void
  refreshList: () => Promise<void>
  deleteConversation: (id: string) => Promise<void>
}

export default function useConversation(): UseConversationResult {
  const authenticatedFetch = useAuthenticatedFetch()
  const { getToken } = useAuth()
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [turns, setTurns] = useState<ConversationTurn[]>([])
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [streamingStage, setStreamingStage] = useState<
    'thinking' | 'generating' | null
  >(null)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [isSending, setIsSending] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  // Reset the panel to a brand-new state: no active conversation, no turns,
  // no streaming, no error. The next sendMessage will create a fresh
  // conversation server-side. Used by ChatPanel's "New chat" button and the
  // CommandPalette's "Start a new chat" command.
  const resetConversation = useCallback(() => {
    setConversation(null)
    setTurns([])
    setStreamingText(null)
    setStreamingStage(null)
    setError(null)
  }, [])

  const refreshList = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/conversations')
      const { data } = (await res.json()) as { data: Conversation[] }
      setConversations(data)
    } catch (err) {
      // Non-fatal — just silently skip list update
      console.warn('Failed to load conversations:', err)
    }
  }, [authenticatedFetch])

  useEffect(() => {
    refreshList()
  }, [refreshList])

  const openConversation = useCallback(
    async (id: string) => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await authenticatedFetch(`/api/conversations/${id}`)
        const { data } = (await res.json()) as { data: ConversationWithTurns }
        setConversation(data.conversation)
        setTurns(data.turns)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to open conversation')
      } finally {
        setIsLoading(false)
      }
    },
    [authenticatedFetch],
  )

  const startNewConversation = useCallback(
    async (model: string, firstMessage?: string, imageUrls: string[] = []) => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await authenticatedFetch('/api/conversations', {
          method: 'POST',
          body: JSON.stringify({ title: 'New conversation', model }),
        })
        const { data } = (await res.json()) as {
          data: { id: string; title: string; model: string }
        }
        const newConv: Conversation = {
          id: data.id,
          user_id: '',
          title: data.title,
          model: data.model,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
        setConversation(newConv)
        setTurns([])
        setConversations((prev) => [newConv, ...prev])

        if (firstMessage) {
          // send in a separate call since we need the new conv id
          await sendMessageImpl(data.id, firstMessage, [], imageUrls)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create conversation')
      } finally {
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authenticatedFetch],
  )

  const sendMessageImpl = useCallback(
    async (
      conversationId: string,
      message: string,
      currentTurns: ConversationTurn[],
      imageUrls: string[] = [],
    ) => {
      setIsSending(true)
      setError(null)
      setStreamingText('')
      setStreamingStage('thinking')

      const optimisticUser: ConversationTurn = {
        id: `optimistic-${Date.now()}`,
        conversation_id: conversationId,
        role: 'user',
        content: message,
        refined_prompt: null,
        generation_id: null,
        image_urls: imageUrls.length > 0 ? JSON.stringify(imageUrls) : null,
        r2_url: null,
        created_at: new Date().toISOString(),
      }
      setTurns([...currentTurns, optimisticUser])

      try {
        const token = await getToken()
        const res = await fetch(
          `${API_BASE}/api/conversations/${conversationId}/messages`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
            },
            body: JSON.stringify({ message, generate: true, image_urls: imageUrls }),
          },
        )

        if (!res.ok || !res.body) {
          let msg = `Request failed: ${res.status}`
          try {
            const j = (await res.json()) as { error?: string }
            if (j.error) msg = j.error
          } catch {
            // ignore
          }
          throw new Error(msg)
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let accumulated = ''
        let finalTurn: SendMessageResponse['assistantTurn'] | null = null
        let streamError: string | null = null

        // SSE frame format: "event: <name>\ndata: <json>\n\n"
        const handleEvent = (eventName: string, payload: string) => {
          let data: unknown
          try {
            data = JSON.parse(payload)
          } catch {
            return
          }

          if (eventName === 'delta') {
            const txt = (data as { text?: string }).text ?? ''
            accumulated += txt
            const visible = extractStreamingExplanation(accumulated)
            // Fall back to raw if model hasn't emitted <explanation> yet
            setStreamingText(visible || accumulated.trim())
          } else if (eventName === 'status') {
            const stage = (data as { stage?: string }).stage
            if (stage === 'generating') setStreamingStage('generating')
          } else if (eventName === 'done') {
            const d = data as { assistantTurn: SendMessageResponse['assistantTurn'] }
            finalTurn = d.assistantTurn
          } else if (eventName === 'error') {
            streamError = (data as { message?: string }).message ?? 'Stream error'
          }
        }

        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          let nlnl = buffer.indexOf('\n\n')
          while (nlnl !== -1) {
            const rawEvent = buffer.slice(0, nlnl)
            buffer = buffer.slice(nlnl + 2)

            let eventName = 'message'
            let dataPayload = ''
            for (const line of rawEvent.split('\n')) {
              if (line.startsWith('event: ')) eventName = line.slice(7).trim()
              else if (line.startsWith('data: ')) dataPayload += line.slice(6)
            }
            if (dataPayload) handleEvent(eventName, dataPayload)
            nlnl = buffer.indexOf('\n\n')
          }
        }

        if (streamError) {
          throw new Error(streamError)
        }
        if (!finalTurn) {
          throw new Error('Stream ended without a final turn')
        }

        const finalAssistant = finalTurn as SendMessageResponse['assistantTurn']
        setTurns((prev) => [
          ...prev.filter((t) => t.id !== optimisticUser.id),
          { ...optimisticUser, id: `user-${finalAssistant.id}` },
          { image_urls: null, ...finalAssistant },
        ])
        refreshList()
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send message'
        setError(msg)
        setTurns((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            conversation_id: conversationId,
            role: 'assistant',
            content: `Error: ${msg}`,
            refined_prompt: null,
            generation_id: null,
            image_urls: null,
            r2_url: null,
            created_at: new Date().toISOString(),
          },
        ])
      } finally {
        setIsSending(false)
        setStreamingText(null)
        setStreamingStage(null)
      }
    },
    [getToken, refreshList],
  )

  const sendMessage = useCallback(
    async (message: string, imageUrls: string[] = []) => {
      if (!conversation) {
        setError('No conversation open')
        return
      }
      await sendMessageImpl(conversation.id, message, turns, imageUrls)
    },
    [conversation, turns, sendMessageImpl],
  )

  const deleteConversation = useCallback(
    async (id: string) => {
      try {
        await authenticatedFetch(`/api/conversations/${id}`, {
          method: 'DELETE',
        })
        setConversations((prev) => prev.filter((c) => c.id !== id))
        if (conversation?.id === id) {
          setConversation(null)
          setTurns([])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete')
      }
    },
    [authenticatedFetch, conversation],
  )

  return {
    conversation,
    turns,
    streamingText,
    streamingStage,
    conversations,
    isSending,
    isLoading,
    error,
    startNewConversation,
    openConversation,
    resetConversation,
    sendMessage,
    clearError,
    refreshList,
    deleteConversation,
  }
}
